import { useEffect, useState, type FormEvent } from 'react';
import type { CreateCustomerTypeInput, CustomerType } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import { useCreateCustomerType, useDeleteCustomerType, useUpdateCustomerType } from './api.js';

interface CustomerTypeFormModalProps {
  isOpen: boolean;
  customerType: CustomerType | null;
  onClose: () => void;
}

interface FormState {
  name: string;
  sortOrder: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = { name: '', sortOrder: '0', isActive: true };

function toFormState(customerType: CustomerType | null): FormState {
  if (!customerType) {
    return EMPTY_FORM;
  }
  return {
    name: customerType.name,
    sortOrder: String(customerType.sortOrder),
    isActive: customerType.isActive,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function CustomerTypeFormModal({
  isOpen,
  customerType: customerTypeProp,
  onClose,
}: CustomerTypeFormModalProps): JSX.Element {
  const [customerType, setCustomerType] = useState<CustomerType | null>(customerTypeProp);
  const isEdit = customerType !== null;
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(customerType));
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateCustomerType();
  const updateMutation = useUpdateCustomerType();
  const deleteMutation = useDeleteCustomerType();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setCustomerType(customerTypeProp);
    }
  }, [isOpen, customerTypeProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(customerType));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, customerType, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save customer type.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: CreateCustomerTypeInput = {
      name: form.name.trim(),
      sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
      isActive: form.isActive,
    };
    if (isEdit) {
      updateMutation.mutate(
        { id: customerType.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setCustomerType(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!customerType) {
      return;
    }
    deleteMutation.mutate(customerType.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit customer type' : 'New customer type'}
        footer={
          <FormActions
            formId="customer-type-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="customer-type-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Name"
              required
              value={form.name}
              maxLength={100}
              autoFocus
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={fieldErrors.name}
            />
            <TextField
              label="Sort order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
              error={fieldErrors.sortOrder}
            />
          </div>

          <CheckboxField
            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete customer type"
        message={`Delete "${customerType?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
